/**
 * Browser permission + device helpers.
 *
 * The SDK has no pre-flight permission/device inspection (its
 * `requestPermissions()` actively prompts), so the wrapper adds a best-effort
 * read-only view for UI to consume before starting.
 */

import { type ProctoringStatus } from '@telusinternationalai/proctoring-sdk'
import type { DeviceSnapshot, PermissionSnapshot, PermissionValue } from './types'

export { isBrowserSupported, isMobileDevice } from '@telusinternationalai/proctoring-sdk'

const queryOne = async (name: string): Promise<PermissionValue> => {
    // `navigator.permissions` is absent in older browsers / non-DOM contexts.
    const permissions = navigator.permissions as Permissions | undefined
    if (!permissions) return 'unsupported'
    try {
        // `camera`/`microphone` aren't in the standard PermissionName union but are
        // supported by Chromium; unsupported browsers reject → `'unsupported'`.
        const status = await permissions.query({ name: name as PermissionName })
        return status.state
    } catch {
        return 'unsupported'
    }
}

/**
 * Best-effort permission snapshot. Screen capture cannot be queried, so it is
 * inferred from the SDK session status (granted once the session is live).
 */
export const queryPermissions = async (
    sdkStatus: ProctoringStatus
): Promise<PermissionSnapshot> => {
    const [camera, microphone] = await Promise.all([queryOne('camera'), queryOne('microphone')])
    const screen = sdkStatus === 'ready' || sdkStatus === 'recording' ? 'granted' : 'prompt'
    return { camera, microphone, screen }
}

/** Enumerate available media inputs. Labels populate only after a permission grant. */
export const enumerateDevices = async (): Promise<DeviceSnapshot> => {
    const empty: DeviceSnapshot = {
        cameras: [],
        microphones: [],
        hasCamera: false,
        hasMicrophone: false,
    }
    const mediaDevices = navigator.mediaDevices as MediaDevices | undefined
    if (!mediaDevices) return empty
    try {
        const devices = await mediaDevices.enumerateDevices()
        const cameras = devices.filter(d => d.kind === 'videoinput')
        const microphones = devices.filter(d => d.kind === 'audioinput')
        return {
            cameras,
            microphones,
            hasCamera: cameras.length > 0,
            hasMicrophone: microphones.length > 0,
        }
    } catch {
        return empty
    }
}
